import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Drawer, Row, Checkbox, Card } from "antd";
import {
  ActionWrapper,
  ActionContentWrapper,
  DesktopContentWrapper,
  MobileContentWrapper,
  TermsWrapper,
  MobileContentContainer,
} from "./styles";
import {
  StyledParagraph,
  StyledTitle,
} from "../../components/CustomAntdComponents/Typography";
import { useFlightSearchStore } from "../../store/flightSearch/flightSearchStore";
import { formatNumberAsCurrency } from "../../utils/helpers";
import {
  calculateFlightTotalFare,
  calculateTicketTotal,
  calculateTicketTotalDiscountPrice,
  calculateTicketTotalOldPrice,
} from "../../components/AvailableFlights/helper";
import { useBookingStore } from "../../store/booking/bookingStore";
import FlightPaymentBreakdown from "../../components/FlightItinerary/FlightPaymentBreakdown";
import FlightFareBreakdown from "../../components/FlightItinerary/FlightFareBreakdown";
import {
  SectionCard,
  SectionCardBody,
  SectionCardHeading,
} from "../PassengerDetails/styles";
import { ReactComponent as ArrowRightIcon } from "../../assets/images/caret-right.svg";
import FlightItinerarySummary from "../../components/FlightItinerary/FlightItinerarySummary";
import useIsMobile from "../../hooks/useIsMobile";
import PassengerList from "../../components/PassengerList";
import BookingContact from "../../components/BookingContact";
import { ActionButtonWrapper } from "../../components/FlightItinerary/styles";
import FlightBookingSummary from "../../components/FlightItinerary/FlightBookingSummary";
import FareRule from "../../components/FareRule";
import { StrikeThroughAmount } from "../../components/AvailableFlights/styles";
import translations from "../PassengerDetails/translations";
import { useI18nContext } from "../../utils/I18n";
import ConditionsRule from "../../components/TermsAndConditions";
import PaymentMethodSelector from "../../components/PaymentComponents/PaymentMethodSelector";
import PaymentTimerCard from "../../components/PaymentComponents/PaymentTimerCard";
import FlightItinerary from "../../components/FlightItinerary";
import { SpaceBetweenAlignContent } from "../../components/CustomAntdComponents/Utitlities";

type Props = {
  handlePayment: () => void;
};

function FlightPayment({ handlePayment }: Props) {
  const {
    selectedFlight,
    formData: { isDomestic, fareRuleAccepted, numberOfAdults, numberOfChildren, numberOfInfants },
  } = useFlightSearchStore();
  const [accept, setAccept] = useState(Boolean(fareRuleAccepted));
  const { isMobile } = useIsMobile();
  const drawerContainerRef = useRef(null);

  const {
    state: { t },
  } = useI18nContext();

  const { bookingPayload, confirmData } = useBookingStore();
  const [selectedMethod, setSelectedMethod] = useState("mpesa");
  const [openDrawer, setOpenDrawer] = useState(false);

  const discountAmount = confirmData
    ? calculateTicketTotalDiscountPrice(confirmData)
    : 0;

  useEffect(() => {
    setAccept(fareRuleAccepted);
  }, [fareRuleAccepted]);

  const handleChange = (e) => {
    setAccept(e.target.checked);
  };
  const { totalTicketAmount } = calculateFlightTotalFare(
    selectedFlight,
    isDomestic
  );
  // Use booking ticket amount to cater for promo discount
  const bookingAmount = bookingPayload
    ? calculateTicketTotal(bookingPayload.TicketingDetails)
    : totalTicketAmount;

  const totalOldPrice = confirmData
    ? calculateTicketTotalOldPrice(confirmData)
    : 0;

  const currency = selectedFlight[0]?.Currency;
  const numberOfPassengers = numberOfAdults + (numberOfChildren || 0) + (numberOfInfants || 0);

  return (
    <div ref={drawerContainerRef}>
      {isMobile ? (
        <Row>
          <Col xs={24} lg={0}>
            <MobileContentContainer>
              <MobileContentWrapper>
                <div style={{ marginBottom: "20px" }}>
                  <StyledParagraph size="default">
                    Booking summary
                  </StyledParagraph>
                </div>
                <Card style={{ marginBottom: 24, paddingBottom: 0 }}>
                  <FlightBookingSummary />
                  <div style={{ height: '1px', background: '#E5E7EB', margin: '14px -24px' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', paddingBottom: 4 }} onClick={() => setOpenDrawer(true)}>
                    <StyledParagraph size="default" style={{ margin: 0 }}>View trip summary</StyledParagraph>
                    <ArrowRightIcon />
                  </div>
                </Card>
                <Card  >
                  <PaymentMethodSelector selectedMethod={selectedMethod} onMethodChange={setSelectedMethod} />
                </Card>
                <PaymentTimerCard />


              </MobileContentWrapper>

              <ActionWrapper>
                {confirmData?.length && confirmData[0]?.IsDiscounted ? (
                  <div className="discount-container">
                    <span>{t(translations).discountApplied}</span>
                    <span className="discount-code-text">
                      {confirmData[0]?.PassengerDetails?.PromoCode}
                    </span>
                    <span className="discount-amount-off-text">
                      (
                      {formatNumberAsCurrency(
                        discountAmount,
                        selectedFlight[0]?.Currency
                      )}{" "}
                      {t(translations).off})
                    </span>
                  </div>
                ) : null}
                <TermsWrapper>
                  <Checkbox onChange={handleChange} checked={accept}>
                    I have read and agree to the <ConditionsRule inline /> and the{" "}
                    <FareRule inline />
                  </Checkbox>
                </TermsWrapper>
                <ActionContentWrapper>
                  <div>
                    <StyledParagraph>Total fare</StyledParagraph>
                    <div className="make-payment-amount-wrapper">
                      <StyledTitle level={3}>
                        {formatNumberAsCurrency(
                          bookingAmount || 0,
                          selectedFlight[0]?.Currency
                        )}
                        &nbsp;
                      </StyledTitle>
                      {confirmData[0]?.IsDiscounted && (
                        <StrikeThroughAmount>
                          {formatNumberAsCurrency(
                            bookingAmount + discountAmount,
                            selectedFlight[0]?.Currency
                          )}
                        </StrikeThroughAmount>
                      )}
                    </div>
                  </div>
                  <ActionButtonWrapper>
                    <Button
                      block
                      size="large"
                      type="primary"
                      htmlType="submit"
                      disabled={!accept}
                      onClick={handlePayment}
                    >
                      Make payment
                    </Button>
                  </ActionButtonWrapper>
                </ActionContentWrapper>
              </ActionWrapper>
            </MobileContentContainer>
          </Col>
        </Row>
      ) : (
        <Row>
          <Col xs={0} lg={24}>
            <DesktopContentWrapper className="container">
              <div style={{ marginBottom: "30px" }}>
                <StyledTitle level={3}>Make payment</StyledTitle>
              </div>
              <Row gutter={40}>
                <Col span={12}>
                  <Card style={{ marginBottom: 24 }}>
                    <PaymentMethodSelector selectedMethod={selectedMethod} onMethodChange={setSelectedMethod} />
                  </Card>
                  <PaymentTimerCard />

                  <TermsWrapper style={{ marginTop: 24, marginBottom: 16 }}>
                    <Checkbox onChange={handleChange} checked={accept}>
                      I have read and agree to the <ConditionsRule inline /> and the{" "}
                      <FareRule inline />
                    </Checkbox>
                  </TermsWrapper>

                  <Button
                    block
                    size="large"
                    type="primary"
                    htmlType="submit"
                    disabled={!accept}
                    onClick={handlePayment}
                  >
                    Make payment
                  </Button>
                </Col>

                <Col span={12}>
                  <SectionCard style={{ padding: '20px', borderRadius: '16px' }}>
                    <StyledTitle level={4} style={{ marginBottom: '36px' }}>Trip summary</StyledTitle>
                    <FlightItinerary hidePriceBreakdown={false} />

                    <div style={{ height: '1px', background: '#E5E7EB', margin: '14px 0' }} />
                    <FlightFareBreakdown hideBreakdown={false} hideCreditAppliedMsg={true} />

                  </SectionCard>
                </Col>
              </Row>
            </DesktopContentWrapper>
          </Col>
        </Row>
      )}

      <Drawer
        title="Trip Summary"
        placement={isMobile ? "bottom" : "right"}
        onClose={() => setOpenDrawer(false)}
        open={openDrawer}
        height="100%"
        width={isMobile ? "100%" : "500px"}
        getContainer={drawerContainerRef.current as HTMLElement}
      >
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
          <div style={{ padding: "0 10px 20px" }}>
            <StyledParagraph size="default">Here are your trip details</StyledParagraph>
          </div>
          <div style={{ flex: 1 }}>
            <FlightItinerary hidePriceBreakdown />
          </div>
          <div style={{ background: '#FFF8F0', padding: "24px", margin: "24px -24px -24px -24px", marginTop: "auto" }}>
            <StyledTitle level={4} style={{ marginBottom: 16 }}>Price breakdown</StyledTitle>
            <FlightFareBreakdown hideBreakdown={false} hideCreditAppliedMsg={true} />
          </div>
        </div>
      </Drawer>
    </div>
  );
}

export default FlightPayment;
